// src/context/AuthContext.jsx
// Clerk-powered AuthContext (replaces Firebase Auth) while keeping the same API shape.
// NOTE: This is NOT Firebase anymore. We can delete Firebase Auth after cutover.

import React, { createContext, useContext, useMemo } from "react";
import { useUser, useAuth as useClerkAuth } from "@clerk/clerk-react";



const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // MOCK AUTH FOR E2E
  if (typeof window !== 'undefined' && window.localStorage.getItem('E2E_TEST_MODE') === 'true') {
     const mockUser = {
       id: 'test-user-123',
       primaryEmailAddress: { emailAddress: 'test@example.com', verification: { status: 'verified' } },
       reload: async () => {},
     };
     const mockCtx = {
         user: mockUser,
         uid: 'test-user-123',
         email: 'test@example.com',
         emailVerified: true,
         isLoaded: true,
         authReady: true,
         isSignedIn: true,
         loading: false,
         getToken: async () => 'mock-token',
         signOut: async () => {},
         sendVerifyEmail: async () => {},
         refreshAuthUser: async () => {},
     };
     return <AuthContext.Provider value={mockCtx}>{children}</AuthContext.Provider>;
  }

  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken, signOut } = useClerkAuth();

  const value = useMemo(() => {
    const email =
      user?.primaryEmailAddress?.emailAddress ||
      user?.emailAddresses?.[0]?.emailAddress ||
      null;

    const emailVerified = user?.primaryEmailAddress?.verification?.status === "verified";

    const sendVerifyEmail = async () => {
      // Trigger Clerk verification email
      if (user?.primaryEmailAddress) {
        await user.primaryEmailAddress.prepareVerification({ strategy: "email_code" });
      }
    };

    const refreshAuthUser = async () => {
      await user?.reload();
    };

    return {
      user: user ?? null,
      uid: user?.id ?? null,
      email,
      emailVerified, // Expose explicitly for legacy checks
      isLoaded,
      authReady: isLoaded,
      isSignedIn: !!isSignedIn,
      loading: !isLoaded,
      getToken,
      signOut,
      sendVerifyEmail,
      refreshAuthUser,
    };
  }, [user, isLoaded, isSignedIn, getToken, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export default AuthContext;
